import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type {
  ReferralOrder,
  ReferralOrdersResponse,
  ReferralOrderCreateRequest,
  ReferralOrderUpdateRequest,
  ReferralOrderCheckoutRequest,
  CheckoutRequiresActionResponse,
  ReferralOrderPricingResponse,
} from './types'

export const ordersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listReferralOrders: builder.query<ReferralOrder[], void>({
      query: () => apiEndpoints.referralOrders.list,
      transformResponse: (response: ReferralOrdersResponse) => response.orders,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ReferralOrder' as const, id })),
              { type: 'ReferralOrder' as const, id: 'LIST' },
            ]
          : [{ type: 'ReferralOrder' as const, id: 'LIST' }],
    }),

    getReferralOrder: builder.query<ReferralOrder, { orderId: string }>({
      query: ({ orderId }) => apiEndpoints.referralOrders.detail(orderId),
      providesTags: (_result, _error, { orderId }) => [
        { type: 'ReferralOrder', id: orderId },
      ],
    }),

    createReferralOrder: builder.mutation<ReferralOrder, ReferralOrderCreateRequest>({
      query: (body) => ({
        url: apiEndpoints.referralOrders.list,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ReferralOrder', id: 'LIST' }],
    }),

    updateReferralOrder: builder.mutation<
      ReferralOrder,
      { orderId: string } & ReferralOrderUpdateRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: apiEndpoints.referralOrders.detail(orderId),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'ReferralOrder', id: orderId },
      ],
    }),

    // Pricing is computed based on the order's provider and practice selections.
    referralOrderPricing: builder.mutation<ReferralOrderPricingResponse, { orderId: string }>({
      query: ({ orderId }) => ({
        url: apiEndpoints.referralOrders.orderPricing(orderId),
        method: 'POST',
      }),
      invalidatesTags: [],
    }),

    // 201 = payment captured (no body), 200 = 3DS required (CheckoutRequiresActionResponse)
    checkoutReferralOrder: builder.mutation<
      CheckoutRequiresActionResponse | undefined,
      { orderId: string } & ReferralOrderCheckoutRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: apiEndpoints.referralOrders.checkout(orderId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { orderId }) => [
        { type: 'ReferralOrder', id: orderId },
        { type: 'ReferralOrderPayment', id: orderId },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useListReferralOrdersQuery,
  useGetReferralOrderQuery,
  useCreateReferralOrderMutation,
  useUpdateReferralOrderMutation,
  useReferralOrderPricingMutation,
  useCheckoutReferralOrderMutation,
} = ordersApi
